import { extendTheme } from "@chakra-ui/react";
import { activeColor, errorColor, primaryColor, successColor } from "./colors";

const disabledBgColor = "gray.200";
const activeWhiteTextColor = "#fff";
const disabledTextColor = "gray.400";

export const theme = extendTheme({
  breakpoints: {
    sm: "40em",
    md: "48em",
    lg: "62em",
    xl: "80em",
    "2xl": "96em",
  },
  components: {
    Button: {
      baseStyle: {
        color: "#fff",
        borderWidth: "1px",
        display: "flex",
        alignItems: "center",
      },
      variants: {
        primary: (props: any) => {
          let bgColor = primaryColor;
          let borderColor = primaryColor;
          let activeBgColor = activeColor;
          let hoverBgColor = "blue.700";

          if (props.colorScheme === "red") {
            bgColor = errorColor;
            borderColor = errorColor;
            activeBgColor = "red.600";
            hoverBgColor = "red.400";
          } else if (props.colorScheme === "green") {
            bgColor = successColor;
            borderColor = successColor;
            activeBgColor = "green.600";
            hoverBgColor = "green.400";
          }

          return {
            bg: bgColor,
            borderColor: borderColor,
            _hover: {
              color: activeWhiteTextColor,
              bg: hoverBgColor,
              borderColor: hoverBgColor,
              _disabled: {
                color: disabledTextColor,
                bg: disabledBgColor,
                borderColor: disabledBgColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: activeWhiteTextColor,
              },
            },
            _active: {
              color: activeWhiteTextColor,
              bg: activeBgColor,
              borderColor: activeBgColor,
              _disabled: {
                color: disabledTextColor,
                bg: disabledBgColor,
                borderColor: disabledBgColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: activeWhiteTextColor,
              },
            },
            _disabled: {
              color: disabledTextColor,
              bg: disabledBgColor,
              borderColor: disabledBgColor,
              opacity: 1,
              svg: {
                color: disabledTextColor,
              },
            },
            svg: {
              color: activeWhiteTextColor,
            },
          };
        },
        secondary: (props: any) => {
          let borderColor = primaryColor;
          let textColor = primaryColor;
          let activeBgColor = activeColor;
          let hoverBgColor = "blue.700";

          if (props.colorScheme === "red") {
            borderColor = errorColor;
            textColor = errorColor;
            activeBgColor = "red.600";
            hoverBgColor = "red.400";
          } else if (props.colorScheme === "green") {
            borderColor = successColor;
            textColor = successColor;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            activeBgColor = "green.600";
            hoverBgColor = "green.400";
          }

          return {
            color: textColor,
            borderColor: borderColor,
            fontWeight: "bold",
            _hover: {
              color: activeWhiteTextColor,
              bg: hoverBgColor,
              borderColor: hoverBgColor,
              _disabled: {
                color: disabledTextColor,
                bg: disabledBgColor,
                borderColor: disabledBgColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: activeWhiteTextColor,
              },
            },
            _active: {
              color: activeWhiteTextColor,
              bg: activeColor,
              borderColor: activeColor,
              _disabled: {
                color: disabledTextColor,
                bg: disabledBgColor,
                borderColor: disabledBgColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: activeWhiteTextColor,
              },
            },
            _disabled: {
              color: disabledTextColor,
              bg: disabledBgColor,
              borderColor: disabledBgColor,
              opacity: 1,
              svg: {
                color: disabledTextColor,
              },
            },
            svg: {
              color: textColor,
            },
          };
        },
        tertiary: (props: any) => {
          let textColor = primaryColor;
          let activeTextColor = activeColor;
          let hoverTextColor = "blue.700";

          if (props.colorScheme === "red") {
            textColor = errorColor;
            activeTextColor = "red.600";
            hoverTextColor = "red.400";
          } else if (props.colorScheme === "green") {
            textColor = successColor;
            activeTextColor = "green.600";
            hoverTextColor = "green.400";
          }

          return {
            color: textColor,
            size: "md",
            padding: "0",
            border: "none",
            height: "22px",
            fontWeight: "bold",
            _hover: {
              color: hoverTextColor,
              _disabled: {
                color: disabledTextColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: hoverTextColor,
              },
            },
            _active: {
              color: activeTextColor,
              _disabled: {
                color: disabledTextColor,
                opacity: 1,
                svg: {
                  color: disabledTextColor,
                },
              },
              svg: {
                color: activeTextColor,
              },
            },
            _disabled: {
              bg: "none",
              _hover: {
                color: activeWhiteTextColor,
                opacity: 1,
                svg: {
                  color: activeWhiteTextColor,
                },
              },
              svg: {
                color: disabledTextColor,
              },
            },
            svg: {
              color: textColor,
            },
          };
        },
        danger: {
          bg: errorColor,
          borderColor: errorColor,
          _hover: {
            color: errorColor,
            bg: "#fff",
            borderColor: errorColor,
          },
        },
        link: {
          borderWidth: "0px",
          textDecoration: "underline",
        },
        pickList: {
          fontWeight: "normal",
          color: primaryColor,
          border: "none",
          borderBottom: "1px solid",
          borderColor: "gray.400",
          borderRadius: "0",
          _hover: {
            bg: "gray.50",
            borderColor: primaryColor,
            _disabled: {
              color: disabledTextColor,
              bg: "gray.100",
              borderColor: "gray.400",
              opacity: 1,
            },
          },
          _disabled: {
            color: disabledTextColor,
            bg: "#EDF2F7",
            borderColor: "gray.400",
            opacity: 1,
          },
        },
      },

      defaultProps: {
        variant: "primary",
      },
    },
    Link: {
      baseStyle: {
        textDecoration: "underline",
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            bg: "#fff",
            borderColor: "#E2E8F0",
            _disabled: {
              opacity: "1",
              color: "gray.400",
              bg: "gray.100",
              borderColor: "gray.200",
              fontWeight: "400",
            },
          },
        },
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          bg: "#fff",
          height: "5",
          width: "5",
          _checked: {
            bg: primaryColor,
            borderColor: "primaryColor",
            _hover: {
              bg: "blue.700",
            },
          },
        },
      },
    },
    Tabs: {
      baseStyle: {
        tab: {
          fontWeight: "bold",
          _selected: {
            color: primaryColor,
          },
        },
      },
    },
    NumberInput: {
      variants: {
        outline: {
          field: {
            bg: "#fff",
            borderColor: "#E2E8F0",
            _disabled: {
              opacity: "1",
              color: "gray.400",
              bg: "gray.100",
              borderColor: "gray.200",
            },
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            bg: "#fff",
            borderColor: "#E2E8F0",
            _disabled: {
              opacity: "1",
              color: "gray.400",
              bg: "gray.100",
              borderColor: "gray.200",
            },
            option: {
              color: "#000",
            },
          },
        },
      },
    },
    Icon: {
      baseStyle: (props: any) => ({
        cursor: props.isDisabled ? "not-allowed" : "pointer",
        color: props.isDisabled ? "gray.400" : props.color,
      }),
    },
    Radio: {
      baseStyle: {
        control: {
          bg: "white",
          _checked: {
            bg: primaryColor,
            borderColor: primaryColor,
            _hover: {
              bg: "blue.700",
              borderColor: "blue.700",
            },
          },
        },
      },
    },
    Table: {
      baseStyle: {
        tr: {
          bg: "white",
          _odd: {
            bg: "gray.100",
          },
        },
      },
    },
  },
});
